import { store } from '../store';
import { setFcmToken } from '../store/slices/FcmSlice';
import { requestFirebaseNotificationPermission } from '../services/firebaseMessaging';
import { getPersistentDeviceId } from './deviceId';
import { request } from './request';

/**
 * Generates and registers FCM token for push notifications
 * 
 * This function:
 * 1. Checks if FCM token already exists in Redux store
 * 2. Registers service worker for Firebase messaging
 * 3. Requests notification permission from user
 * 4. Registers token with backend API
 * 5. Stores token in Redux state
 * 
 * @returns Promise<string | null> - The FCM token if successful, null otherwise
 * 
 * Usage:
 * - Call after successful login/signup to enable push notifications
 * - Call when user enables notifications in settings
 * - Automatically checks for existing token to avoid duplicate registration
 */
export const generateAndRegisterFcmToken = async (): Promise<string | null> => {
  const currentState = store.getState();
  
  // Return existing token if already present
  if (currentState.fcm?.token) {
    console.info('[FCM] token already present');
    return currentState.fcm.token;
  }
  
  // Check if service worker is supported
  if (!('serviceWorker' in navigator)) {
    console.warn('[FCM] Service worker not supported');
    return null;
  }
  
  try {
    // Register service worker for Firebase messaging
    const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
    
    // Request notification permission and get FCM token
    const token = await requestFirebaseNotificationPermission(registration);
    
    if (token) {
      // Store token in Redux state
      store.dispatch(setFcmToken(token));
      
      // Register token with backend API
      const deviceId = getPersistentDeviceId();
      await request('/profile/fcm/register-token', {
        method: 'POST',
        body: { fcmToken: token, platform: 'web', deviceId },
        authorization: true,
      }).catch(console.error);
      
      console.info('[FCM] Token generated and registered successfully');
      return token;
    }
  } catch (err) {
    console.error('[FCM] Failed to generate FCM token', err);
  }
  
  return null;
};

